import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah, rupiahShort } from "@/lib/format";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, CartesianGrid,
} from "recharts";
import { Banknote, ArrowLeftRight, QrCode, TrendingUp, Receipt, Wallet, AlertTriangle, Building2, ShieldAlert } from "lucide-react";

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/dashboard").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const today = new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  if (!data) return (
    <>
      <PageHeader title="Dashboard" subtitle={today} />
      <div className="flex-1 flex items-center justify-center text-ui-muted">Memuat…</div>
    </>
  );

  const cards = [
    { label: "Pendapatan Hari Ini", value: rupiah(data.total_revenue), icon: TrendingUp, color: "text-brand-readable", bg: "bg-brand-soft" },
    { label: "Laba Kotor", value: rupiah(data.gross_profit || 0), icon: Wallet, color: "text-brand-readable", bg: "bg-brand-soft" },
    { label: "Transaksi", value: data.tx_count, icon: Receipt, color: "text-accent-readable", bg: "bg-accent-soft" },
    { label: "Rata-rata / Transaksi", value: rupiah(data.avg_transaction), icon: Wallet, color: "text-accent-readable", bg: "bg-accent-soft" },
    { label: "Total Kembalian", value: rupiah(data.total_change), icon: Banknote, color: "text-warning-readable", bg: "bg-warning-soft" },
  ];

  const methods = [
    { key: "tunai", label: "Tunai", icon: Banknote, color: "var(--masivi-primary)" },
    { key: "transfer", label: "Transfer", icon: ArrowLeftRight, color: "var(--masivi-secondary)" },
    { key: "qris", label: "QRIS", icon: QrCode, color: "var(--masivi-accent)" },
  ];
  const methodTotal = methods.reduce((s, m) => s + (data.method_breakdown[m.key] || 0), 0) || 1;

  return (
    <>
      <PageHeader title="Dashboard" subtitle={today} />
      <div className="flex-1 min-w-0 overflow-y-auto p-3 sm:p-4 xl:p-5" data-testid="sales-dashboard">
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2 sm:gap-3 xl:gap-4 mb-4 sm:mb-5 xl:mb-6">
          {cards.map((c) => (
            <div key={c.label} className="min-w-0 rounded-xl border border-ui-line bg-ui-soft p-2.5 sm:p-3 xl:p-4">
              <div className="flex items-center justify-between">
                <span className="min-w-0 text-[10px] sm:text-[11px] xl:text-xs text-ui-muted uppercase tracking-wide">{c.label}</span>
                <div className={`h-7 w-7 sm:h-8 sm:w-8 xl:h-9 xl:w-9 shrink-0 rounded-lg ${c.bg} flex items-center justify-center`}>
                  <c.icon className={`h-4 w-4 xl:h-5 xl:w-5 ${c.color}`} />
                </div>
              </div>
              <div className="break-all font-mono font-extrabold text-base sm:text-lg xl:text-2xl mt-1.5 sm:mt-2">{c.value}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-4 xl:gap-6 mb-4 sm:mb-5 xl:mb-6">
          <section className="min-w-0 rounded-xl border border-ui-line bg-ui-soft p-3 sm:p-3 xl:p-4">
            <h3 className="text-sm xl:text-base font-heading font-semibold mb-2 sm:mb-3 flex items-center gap-2"><Building2 className="h-4 w-4 text-brand-readable" /> Ringkasan per Cabang</h3>
            <div className="overflow-x-auto"><table className="w-full text-xs sm:text-[11px] xl:text-sm"><thead className="text-[10px] sm:text-[10px] xl:text-xs text-ui-muted"><tr><th className="text-left py-2">Cabang</th><th className="text-right">Transaksi</th><th className="text-right">Penjualan</th><th className="text-right">Laba Kotor</th></tr></thead><tbody>{(data.branches || []).map((branch) => <tr key={branch.store_id} className="border-t border-ui-line"><td className="py-2 font-medium">{branch.store_name}</td><td className="text-right font-mono">{branch.transactions}</td><td className="text-right font-mono whitespace-nowrap">{rupiah(branch.revenue)}</td><td className={`text-right font-mono whitespace-nowrap ${branch.gross_profit < 0 ? "text-danger-readable" : "text-brand-readable"}`}>{rupiah(branch.gross_profit)}</td></tr>)}</tbody></table></div>
          </section>
          <section className="min-w-0 rounded-xl border border-ui-line bg-ui-soft p-3 sm:p-3 xl:p-4">
            <div className="flex items-center justify-between gap-3 mb-2 sm:mb-3"><h3 className="text-sm xl:text-base font-heading font-semibold flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-danger-readable" /> Peringatan Anomali</h3><span className="text-[10px] sm:text-xs rounded-full bg-danger-soft text-danger-readable px-2 py-1">{(data.anomalies || []).length}</span></div>
            {(data.anomalies || []).length === 0 ? <p className="text-xs sm:text-sm text-ui-muted py-5 sm:py-8 text-center">Tidak ada anomali terdeteksi hari ini.</p> : <div className="max-h-60 xl:max-h-72 overflow-y-auto space-y-1.5">{data.anomalies.map((item, index) => <div key={`${item.type}-${item.detail}-${index}`} className={`rounded-lg border p-2 ${item.severity === "high" ? "border-danger/40 bg-danger-soft" : "border-warning/40 bg-warning-soft"}`}><div className="flex justify-between gap-2"><strong className="text-xs sm:text-sm">{item.title}</strong>{item.amount ? <span className="break-all text-right font-mono text-xs sm:text-sm">{rupiah(item.amount)}</span> : null}</div><div className="text-[11px] sm:text-xs text-ui-muted mt-0.5">{item.detail}{item.store ? ` · ${item.store}` : ""}</div></div>)}</div>}
            <p className="text-[9px] sm:text-[10px] text-ui-muted mt-2 sm:mt-3">Refund besar mulai {rupiah(data.anomaly_settings?.large_refund_amount || 500000)} · Piutang jatuh tempo setelah {data.anomaly_settings?.receivable_due_days || 30} hari.</p>
          </section>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 xl:gap-6 mb-4 sm:mb-5 xl:mb-6">
          <div className="sm:col-span-2 min-w-0 rounded-xl border border-ui-line bg-ui-soft p-3 xl:p-4">
            <h3 className="text-sm xl:text-base font-heading font-semibold mb-2 sm:mb-3 xl:mb-4">Tren Penjualan 7 Hari</h3>
            <div className="h-[190px] sm:h-[210px] xl:h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--masivi-border)" />
                <XAxis dataKey="date" stroke="var(--masivi-card-muted)" fontSize={12} />
                <YAxis stroke="var(--masivi-card-muted)" fontSize={12} tickFormatter={rupiahShort} width={60} />
                <Tooltip contentStyle={{ background: "var(--masivi-card-bg)", color: "var(--masivi-card-text)", border: "1px solid var(--masivi-border)", borderRadius: 12 }} formatter={(v) => rupiah(v)} />
                <Line type="monotone" dataKey="total" stroke="var(--masivi-primary)" strokeWidth={3} dot={{ fill: "var(--masivi-primary)" }} />
              </LineChart>
            </ResponsiveContainer>
            </div>
          </div>

          <div className="min-w-0 rounded-xl border border-ui-line bg-ui-soft p-3 xl:p-4">
            <h3 className="text-sm xl:text-base font-heading font-semibold mb-3 xl:mb-4">Metode Pembayaran</h3>
            <div className="space-y-3 xl:space-y-4">
              {methods.map((m) => {
                const val = data.method_breakdown[m.key] || 0;
                const pct = Math.round((val / methodTotal) * 100);
                return (
                  <div key={m.key}>
                    <div className="flex items-center justify-between gap-1 text-xs xl:text-sm mb-1">
                      <span className="flex items-center gap-1.5"><m.icon className="h-4 w-4 shrink-0" style={{ color: m.color }} /> {m.label}</span>
                      <span className="break-all text-right font-mono">{rupiah(val)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-ui-muted-surface overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: m.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 xl:gap-6">
          <div className="min-w-0 rounded-xl border border-ui-line bg-ui-soft p-3 xl:p-4">
            <h3 className="text-sm xl:text-base font-heading font-semibold mb-3 xl:mb-4">Produk Terlaris Hari Ini</h3>
            {data.top_products.length === 0 ? (
              <p className="text-ui-muted text-sm py-8 text-center">Belum ada penjualan hari ini</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.top_products} layout="vertical" margin={{ left: 10 }}>
                  <XAxis type="number" stroke="var(--masivi-card-muted)" fontSize={12} hide />
                  <YAxis type="category" dataKey="name" stroke="var(--masivi-card-muted)" fontSize={11} width={110} />
                  <Tooltip contentStyle={{ background: "var(--masivi-card-bg)", color: "var(--masivi-card-text)", border: "1px solid var(--masivi-border)", borderRadius: 12 }} formatter={(v, n) => n === "qty" ? [v + " pcs", "Terjual"] : rupiah(v)} />
                  <Bar dataKey="qty" fill="var(--masivi-accent)" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="min-w-0 rounded-xl border border-ui-line bg-ui-soft p-3 xl:p-4">
            <h3 className="text-sm xl:text-base font-heading font-semibold mb-3 xl:mb-4 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-warning-readable" /> Stok Menipis</h3>
            {data.low_stock.length === 0 ? (
              <p className="text-ui-muted text-sm py-8 text-center">Semua stok aman 👍</p>
            ) : (
              <div className="space-y-2 max-h-[260px] overflow-y-auto">
                {data.low_stock.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg bg-ui-soft/70 border border-ui-line px-3 py-2">
                    <span className="text-sm">{p.name}</span>
                    <span className="font-mono text-warning-readable text-sm">sisa {p.stock}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
